import React, { useEffect, useState, useCallback } from 'react';
import { listVideos } from '../../api/videos';
import {
  getKnownBillboardDevices,
  launchAdToBillboard,
  listBillboardAssignments,
  updateBillboardAssignment,
  removeBillboardAssignment,
  registerBillboardDevice,
  updateCampaignStatus,
  type BillboardAssignmentDTO,
  type DeviceTelemetrySnapshot,
  type CampaignStatus,
} from '../../api/billboard';

interface LaunchableAd {
  _id: string;
  title: string;
  creativeType?: string;
  imageUrl?: string;
  campaignStatus?: CampaignStatus;
}

type ListVideosResponse =
  | LaunchableAd[]
  | { items: LaunchableAd[]; total?: number }
  | null
  | undefined;

export const BillboardLaunchPanel: React.FC = () => {
  const [ads, setAds] = useState<LaunchableAd[]>([]);
  const [devices, setDevices] = useState<DeviceTelemetrySnapshot[]>([]);
  const [assignments, setAssignments] = useState<BillboardAssignmentDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newDeviceId, setNewDeviceId] = useState('');
  const [addingDevice, setAddingDevice] = useState(false);
  const [schedulingAdId, setSchedulingAdId] = useState<string | null>(null);
  const [schedStart, setSchedStart] = useState('');
  const [schedEnd, setSchedEnd] = useState('');

  const clearFlash = () => {
    setError(null);
    setSuccess(null);
  };

  const refresh = useCallback(async () => {
    try {
      const [adsData, devicesData, assignData] = await Promise.all([
        listVideos({ pageSize: 100 }) as Promise<ListVideosResponse>,
        getKnownBillboardDevices(),
        listBillboardAssignments(),
      ]);

      if (Array.isArray(adsData)) {
        setAds(adsData as LaunchableAd[]);
      } else if (adsData && !Array.isArray(adsData) && Array.isArray(adsData.items)) {
        setAds(adsData.items as LaunchableAd[]);
      } else {
        setAds([]);
      }

      setDevices(devicesData);
      setAssignments(assignData.items ?? []);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 20_000);
    return () => clearInterval(id);
  }, [refresh]);

  const handleLaunch = async (
    adId: string,
    deviceId?: string,
    scheduledStart?: string,
    scheduledEnd?: string,
  ) => {
    clearFlash();
    setBusy(adId);
    try {
      const result = await launchAdToBillboard(adId, deviceId, undefined, scheduledStart, scheduledEnd);
      setSuccess(result.message);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Launch failed');
    } finally {
      setBusy(null);
    }
  };

  const handlePause = async (id: string) => {
    clearFlash();
    setBusy(id);
    try {
      await updateBillboardAssignment(id, { status: 'paused' });
      setSuccess('Ad paused');
      await refresh();
    } catch {
      setError('Failed to pause');
    } finally {
      setBusy(null);
    }
  };

  const handleResume = async (id: string) => {
    clearFlash();
    setBusy(id);
    try {
      await updateBillboardAssignment(id, { status: 'active' });
      setSuccess('Ad resumed');
      await refresh();
    } catch {
      setError('Failed to resume');
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (id: string) => {
    clearFlash();
    setBusy(id);
    try {
      await removeBillboardAssignment(id);
      setSuccess('Ad removed from billboard');
      await refresh();
    } catch {
      setError('Failed to remove');
    } finally {
      setBusy(null);
    }
  };

  const handleAddDevice = async () => {
    const trimmed = newDeviceId.trim();
    if (!trimmed) return;
    clearFlash();
    setAddingDevice(true);
    try {
      await registerBillboardDevice(trimmed);
      setSuccess(`Billboard "${trimmed}" registered`);
      setNewDeviceId('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register device');
    } finally {
      setAddingDevice(false);
    }
  };

  const handleStatusChange = async (adId: string, target: CampaignStatus) => {
    clearFlash();
    setBusy(adId);
    try {
      await updateCampaignStatus(adId, target);
      setSuccess(`Campaign status updated to "${target}"`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status transition failed');
    } finally {
      setBusy(null);
    }
  };

  const isAdLive = (adId: string) =>
    assignments.some((a) => a.adId === adId && a.status === 'active');

  if (loading) {
    return (
      <div className="p-4 text-sm text-slate-500 dark:text-slate-400">Loading billboard launch manager...</div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Billboard Launch Manager</h3>
        <button
          onClick={() => void refresh()}
          className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2.5 py-1 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50"
        >
          Refresh
        </button>
      </div>

      {/* Seed / register a new billboard device */}
      <div className="flex items-end gap-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 px-4 py-3">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Add a billboard</label>
          <input
            type="text"
            placeholder="e.g. billboard-2, times-square-north"
            value={newDeviceId}
            onChange={(e) => setNewDeviceId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleAddDevice(); }}
            className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => void handleAddDevice()}
          disabled={addingDevice || !newDeviceId.trim()}
          className="inline-flex items-center rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-slate-700 disabled:opacity-40"
        >
          {addingDevice ? 'Adding...' : 'Register Billboard'}
        </button>
      </div>
      {devices.length > 0 && (
        <p className="text-xs text-slate-400">
          {devices.length} billboard{devices.length !== 1 ? 's' : ''} registered:{' '}
          {devices.map((d) => d.deviceId).join(', ')}
        </p>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-700 ring-1 ring-emerald-200">
          {success}
        </div>
      )}

      {/* Available Ads with Launch buttons */}
      <div>
        <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Available Ads</h4>
        {ads.length === 0 ? (
          <p className="text-sm text-slate-400">No ads found. Create an ad first.</p>
        ) : (
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            {ads.map((ad) => {
              const live = isAdLive(ad._id);
              const status = ad.campaignStatus ?? 'draft';
              const canLaunch = status === 'approved' || status === 'live';

              const statusBadge: Record<string, { bg: string; text: string; label: string }> = {
                draft:    { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-400', label: 'Draft' },
                review:   { bg: 'bg-amber-100', text: 'text-amber-700', label: 'In Review' },
                approved: { bg: 'bg-blue-100',  text: 'text-blue-700',  label: 'Approved' },
                live:     { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Live' },
                ended:    { bg: 'bg-red-100',   text: 'text-red-600',   label: 'Ended' },
              };
              const badge = statusBadge[status] ?? statusBadge.draft;

              // Next logical transition for the status button
              const nextAction: Record<string, { label: string; target: CampaignStatus } | null> = {
                draft:    { label: 'Submit for Review', target: 'review' },
                review:   { label: 'Approve', target: 'approved' },
                approved: null, // launch buttons handle this
                live:     { label: 'End Campaign', target: 'ended' },
                ended:    { label: 'Restart as Draft', target: 'draft' },
              };
              const next = nextAction[status] ?? null;
              const canReject = status === 'review';

              return (
                <div
                  key={ad._id}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  {/* Thumbnail */}
                  <div className="h-10 w-14 flex-shrink-0 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
                    {ad.imageUrl ? (
                      <img
                        src={ad.imageUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">
                        {ad.creativeType ?? 'ad'}
                      </div>
                    )}
                  </div>

                  {/* Title + status */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                      {ad.title || 'Untitled'}
                    </p>
                    <div className="flex items-center gap-1.5 text-xs text-slate-400">
                      <span>{ad.creativeType ?? 'text'}</span>
                      <span
                        className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${badge.bg} ${badge.text}`}
                      >
                        {badge.label}
                      </span>
                      {live && status !== 'live' && (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                          ON AIR
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Campaign status action + Launch buttons */}
                  <div className="flex flex-shrink-0 items-center gap-2">
                    {next && (
                      <button
                        onClick={() => void handleStatusChange(ad._id, next.target)}
                        disabled={busy === ad._id}
                        className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 disabled:opacity-50"
                      >
                        {next.label}
                      </button>
                    )}

                    {canReject && (
                      <button
                        onClick={() => void handleStatusChange(ad._id, 'draft')}
                        disabled={busy === ad._id}
                        className="rounded border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    )}

                    {canLaunch && devices.length > 0 && (
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            void handleLaunch(ad._id, e.target.value);
                            e.target.value = '';
                          }
                        }}
                        defaultValue=""
                        disabled={busy === ad._id}
                        className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="" disabled>
                          Launch to...
                        </option>
                        {devices.map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.deviceId}
                          </option>
                        ))}
                      </select>
                    )}

                    {canLaunch && (
                      <button
                        onClick={() => void handleLaunch(ad._id)}
                        disabled={busy === ad._id}
                        className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                      >
                        {busy === ad._id ? '...' : 'Launch to All'}
                      </button>
                    )}

                    {canLaunch && (
                      <button
                        onClick={() => {
                          setSchedulingAdId(schedulingAdId === ad._id ? null : ad._id);
                          setSchedStart('');
                          setSchedEnd('');
                        }}
                        className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-50"
                      >
                        Schedule
                      </button>
                    )}
                  </div>

                  {/* Inline schedule picker */}
                  {schedulingAdId === ad._id && (
                    <div className="col-span-full mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-4 py-3">
                      <div>
                        <label className="mb-0.5 block text-[10px] font-medium text-slate-500 dark:text-slate-400">Start</label>
                        <input
                          type="datetime-local"
                          value={schedStart}
                          onChange={(e) => setSchedStart(e.target.value)}
                          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs"
                        />
                      </div>
                      <div>
                        <label className="mb-0.5 block text-[10px] font-medium text-slate-500 dark:text-slate-400">End</label>
                        <input
                          type="datetime-local"
                          value={schedEnd}
                          onChange={(e) => setSchedEnd(e.target.value)}
                          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs"
                        />
                      </div>
                      <button
                        onClick={() => {
                          void handleLaunch(
                            ad._id,
                            undefined,
                            schedStart ? new Date(schedStart).toISOString() : undefined,
                            schedEnd ? new Date(schedEnd).toISOString() : undefined,
                          );
                          setSchedulingAdId(null);
                        }}
                        disabled={busy === ad._id}
                        className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Schedule Launch
                      </button>
                      <button
                        onClick={() => setSchedulingAdId(null)}
                        className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-500 dark:text-slate-400 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Current Assignments */}
      {assignments.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Active Assignments</h4>
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            {assignments.map((a) => {
              const fmtDate = (d: string | null) =>
                d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;
              const startLabel = fmtDate(a.scheduledStart);
              const endLabel = fmtDate(a.scheduledEnd);

              return (
                <div
                  key={a._id}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                      {a.adTitle ?? 'Ad'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {a.deviceId === '*' ? 'All billboards' : a.deviceId}
                      {' · '}
                      <span
                        className={
                          a.status === 'active'
                            ? 'text-emerald-600'
                            : a.status === 'paused'
                              ? 'text-amber-500'
                              : 'text-slate-400'
                        }
                      >
                        {a.status}
                      </span>
                      {' · weight '}
                      {a.weight}
                      {(startLabel || endLabel) && (
                        <span className="ml-1 text-slate-500 dark:text-slate-400">
                          · {startLabel ? `from ${startLabel}` : 'now'}
                          {endLabel ? ` until ${endLabel}` : ''}
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="flex flex-shrink-0 items-center gap-1.5">
                    {a.status === 'active' && (
                      <button
                        onClick={() => void handlePause(a._id)}
                        disabled={busy === a._id}
                        className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                      >
                        Pause
                      </button>
                    )}
                    {a.status === 'paused' && (
                      <button
                        onClick={() => void handleResume(a._id)}
                        disabled={busy === a._id}
                        className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                      >
                        Resume
                      </button>
                    )}
                    <button
                      onClick={() => void handleRemove(a._id)}
                      disabled={busy === a._id}
                      className="rounded border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BillboardLaunchPanel;
